/**
 * A masked screen replay of a traced play (ADR 0035, decision 5): Sentry Session Replay with every
 * text and input masked (the room shows chat, names and the invite link), images and media
 * blocked, and the game's canvas recorded at low quality, so what happened in the game and where
 * the player tapped are visible and nothing anyone wrote is. Started only for a traced play, and
 * only where crash reports are on (a Sentry client exists). No compression worker, so no blob:
 * worker in the CSP. A failure means no replay; the play goes on.
 */

export interface Replay {
  /** The replay's id once it has started (linked from the trace's summary). */
  id(): string | undefined;
  stop(): Promise<void>;
}

type ReplayIntegration = {
  start(): void;
  stop(): Promise<void>;
  getReplayId(): string | undefined;
};
export type ReplaySdk = {
  getClient(): unknown;
  getReplay(): ReplayIntegration | undefined;
  addIntegration(integration: unknown): void;
  replayIntegration(options: Record<string, unknown>): ReplayIntegration;
  replayCanvasIntegration(options: Record<string, unknown>): unknown;
};

/** The replay's settings: everything a person wrote or showed is hidden. */
export const MASKED = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
  stickySession: false,
  useCompression: false,
  networkDetailAllowUrls: [],
} as const;

export async function startReplay(
  load: () => Promise<ReplaySdk> = () => import("./sentry-replay.js"),
): Promise<Replay | null> {
  try {
    const sdk = await load();
    if (!sdk.getClient()) return null; // crash reports are off on this build
    let replay = sdk.getReplay();
    if (!replay) {
      replay = sdk.replayIntegration({ ...MASKED });
      sdk.addIntegration(replay);
      sdk.addIntegration(sdk.replayCanvasIntegration({ quality: "low" }));
    }
    replay.start();
    const running = replay;
    return { id: () => running.getReplayId(), stop: () => running.stop() };
  } catch {
    return null;
  }
}
